import React from "react";
import {Link, SelectionMode, ShimmeredDetailsList, Stack, Text, useTheme} from "@fluentui/react";
import {useHistory} from "react-router-dom";
import {toast} from "react-toastify";
import CurrencyFormat from "react-currency-format";
import {ColumnGenerator, CurrencyColumnGenerator} from "../../logic/Columns/ColumnGenerator";
import {useGetProductPurchaseHistoryQuery} from "../../store/Api";
import {useLazyResolveRecordUrlQuery} from "../../store/endpoints/RecordLinksEndpoints";
import {IPurchaseHistoryItem, IPurchaseHistorySourceType} from "../../models/Extension";

const RECORD_TYPE_OPPORTUNITY = 'Opportunity';

interface IProductPurchaseHistoryProps {
    catalog?: string;
    productId?: string;
}

const sourceLabel = (sourceType: IPurchaseHistorySourceType): string => {
    switch (sourceType) {
        case 'storefront': return 'Storefront';
        case 'opportunity': return 'CRM';
        case 'both': return 'Storefront + CRM';
        default: return sourceType;
    }
};

const storefrontReference = (item: IPurchaseHistoryItem): string | undefined =>
    item.orderNumber != null ? `Order #${item.orderNumber}` : undefined;

const opportunityReference = (item: IPurchaseHistoryItem): string | undefined =>
    item.orderName ?? (item.opportunityId ? `Opportunity ${item.opportunityId}` : undefined);

const ReferenceCell: React.FC<{
    item: IPurchaseHistoryItem,
    openOrder: (orderId: string) => void,
    openOpportunity: (folioId: string, opportunityId: string) => void
}> = ({item, openOrder, openOpportunity}) => {
    const orderRef = storefrontReference(item);
    const opportunityRef = opportunityReference(item);
    const {storefrontOrderId, folioId, opportunityId} = item;
    const orderLink = orderRef && storefrontOrderId
        ? <Link onClick={() => openOrder(storefrontOrderId)}>{orderRef}</Link>
        : orderRef && <span>{orderRef}</span>;
    const opportunityLink = opportunityRef && folioId && opportunityId
        ? <Link onClick={() => openOpportunity(folioId, opportunityId)}>{opportunityRef}</Link>
        : opportunityRef && <span>{opportunityRef}</span>;

    if (item.sourceType === 'both') {
        // A merged row is one sale with two records; show both references so either can be opened.
        return <span>{orderLink}{orderLink && opportunityLink && ' · '}{opportunityLink}</span>;
    }
    if (item.sourceType === 'storefront') return <span>{orderLink ?? ''}</span>;
    return <span>{opportunityLink ?? ''}</span>;
};

const ProductPurchaseHistory: React.FC<IProductPurchaseHistoryProps> = (props) => {
    const theme = useTheme();
    const history = useHistory();
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();
    const query = useGetProductPurchaseHistoryQuery(
        {catalog: props.catalog ?? '', productId: props.productId ?? ''},
        {skip: !props.catalog || !props.productId}
    );

    const openOrder = (orderId: string) =>
        history.push(`/storefront/orders?orderId=${encodeURIComponent(orderId)}`);

    // Opportunities live in the folio's CRM, not in Adaptive Catalog, so resolve a live deep-link URL and
    // open it there. Same mechanism and toast flow as ExtensionActivities' record references.
    const openOpportunity = async (folioId: string, opportunityId: string) => {
        const toastId = toast.loading('Opening record in the CRM…');
        try {
            const res = await resolveRecordUrl({folioId, recordType: RECORD_TYPE_OPPORTUNITY, recordId: opportunityId}).unwrap();
            if (!res.supported) {
                toast.update(toastId, {type: 'error', render: "This record can't be opened in the CRM", isLoading: false, autoClose: 4000});
            } else if (res.success && res.url) {
                window.open(res.url, '_blank', 'noopener');
                toast.dismiss(toastId);
            } else {
                toast.update(toastId, {type: 'error', render: res.error?.trim() || 'Could not open record in the CRM', isLoading: false, autoClose: 5000});
            }
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to open record', isLoading: false, autoClose: 4000});
        }
    };

    const items = query.data?.items ?? [];
    const total = query.data?.total ?? items.length;
    const loading = query.isLoading || query.isFetching;

    const columns = [
        ColumnGenerator('Date', 'date', 100, 110, (item: IPurchaseHistoryItem) =>
            <>{item.date ? new Date(item.date).toLocaleDateString() : ''}</>),
        ColumnGenerator('Source', 'sourceType', 90, 130, (item: IPurchaseHistoryItem) =>
            <>{sourceLabel(item.sourceType)}</>),
        ColumnGenerator('Reference', 'reference', 120, 200, (item: IPurchaseHistoryItem) =>
            <ReferenceCell item={item} openOrder={openOrder} openOpportunity={(f, o) => { void openOpportunity(f, o); }}/>),
        ColumnGenerator('Company', 'companyName', 140, 200, (item: IPurchaseHistoryItem) =>
            <>{item.companyName ?? ''}</>),
        ColumnGenerator('Sales Rep', 'salesRep', 120, 160, (item: IPurchaseHistoryItem) =>
            <>{item.salesRep ?? ''}</>),
        ColumnGenerator('Qty', 'quantity', 50, 60, (item: IPurchaseHistoryItem) =>
            <div style={{textAlign: 'right'}}>{item.quantity}</div>),
        CurrencyColumnGenerator('Unit Price', 'price', 90, 110),
        ColumnGenerator('Total', 'total', 90, 110, (item: IPurchaseHistoryItem) => {
            const rowTotal = item.revenue ?? (item.price != null ? item.price * item.quantity : undefined);
            return rowTotal != null
                ? <div style={{textAlign: 'right'}}>
                    <CurrencyFormat
                        value={rowTotal}
                        displayType="text"
                        thousandSeparator=","
                        prefix="$"
                        fixedDecimalScale={true}
                        decimalScale={2}
                    />
                </div>
                : <></>;
        }),
    ];

    if (!loading && items.length === 0) {
        return <Text styles={{root: {color: theme.palette.neutralSecondary}}}>No purchase history found for this product.</Text>;
    }

    return (
        <Stack tokens={{childrenGap: '1em'}}>
            <ShimmeredDetailsList
                items={items}
                columns={columns}
                enableShimmer={loading}
                selectionMode={SelectionMode.none}
            />
            {total > items.length &&
                <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>
                    Showing {items.length} of {total}
                </Text>}
        </Stack>
    );
};

export default ProductPurchaseHistory;
